import React from 'react';
import { useData } from '../../context/DataContext';
import { computeGroupStats } from '../../utils/calculations';

export const SectionPerformanceTable = () => {
  const { currentBranchFilter, getScopedStudents } = useData();

  const scoped = getScopedStudents();
  const isScoped = currentBranchFilter !== 'all';

  if (!isScoped) return null;

  const sectionGroups = {};
  scoped.forEach(s => {
    const key = `${s.registeredGrade}-${s.section || 'Unassigned'}`;
    if (!sectionGroups[key]) {
      sectionGroups[key] = { grade: s.registeredGrade, section: s.section || 'Unassigned', students: [] };
    }
    sectionGroups[key].students.push(s);
  });

  const sectionKeys = Object.keys(sectionGroups).sort((a, b) => {
    const ga = sectionGroups[a];
    const gb = sectionGroups[b];
    return ga.grade - gb.grade || String(ga.section).localeCompare(String(gb.section));
  });

  const pctOf = (v, n) => (n > 0 ? Math.round((v / n) * 100) + '%' : '0%');
  const scoreCell = (v, n, color) => (
    <td className="p-3 border font-bold" style={{ color: color }}>
      {n > 0 ? Math.round(v) + '%' : '—'}
    </td>
  );

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-1">Section-wise Performance</h3>
          <p className="text-sm text-gray-600">Every grade &amp; section in this branch, sorted by grade then section.</p>
          <p className="text-xs italic text-gray-400 mt-0.5">*A single weak level (60-79%) is forgiven if every other level is ≥80%.</p>
        </div>
        <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100 w-fit">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#2a6a4a' }}></span>
            <span>At Grade Level</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#d4870a' }}></span>
            <span>Bridge the Gap</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#dc3545' }}></span>
            <span>Remediation Need</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full border-collapse text-sm text-center">
          <thead>
            <tr className="text-white text-xs font-bold">
              <th className="p-3 border text-left" style={{ background: '#1e3a5f' }}>SECTION</th>
              <th className="p-3 border" style={{ background: '#1e3a5f' }}>ASSESSED</th>
              <th className="p-3 border" style={{ background: '#1e3a5f' }}>OVERALL AVG</th>
              <th className="p-3 border" style={{ background: '#2a6a4a' }}>ENGLISH AVG</th>
              <th className="p-3 border" style={{ background: '#1e3a9a' }}>MATH AVG</th>
              <th className="p-3 border" style={{ background: '#2a6a4a' }}>AT GRADE LEVEL</th>
              <th className="p-3 border" style={{ background: '#d4870a' }}>BRIDGE</th>
              <th className="p-3 border" style={{ background: '#dc3545' }}>REMEDIATION</th>
            </tr>
          </thead>
          <tbody>
            {sectionKeys.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-10 text-gray-400 italic">
                  No section data available.
                </td>
              </tr>
            ) : (
              sectionKeys.map(key => {
                const group = sectionGroups[key];
                const stats = computeGroupStats(group.students);
                return (
                  <tr key={key} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="p-3 border text-left font-semibold text-gray-800">
                      Grade {group.grade} - {group.section}
                    </td>
                    <td className="p-3 border text-gray-700">
                      {stats.participated}/{stats.total}
                    </td>
                    {scoreCell(stats.avgOverall, stats.participated, '#1e3a5f')}
                    {scoreCell(stats.avgEnglish, stats.participated, '#2a6a4a')}
                    {scoreCell(stats.avgMath, stats.participated, '#1e3a9a')}
                    <td className="p-3 border" style={{ color: '#2a6a4a' }}>{pctOf(stats.comp, stats.participated)}</td>
                    <td className="p-3 border" style={{ color: '#d4870a' }}>{pctOf(stats.bridge, stats.participated)}</td>
                    <td className="p-3 border" style={{ color: '#dc3545' }}>{pctOf(stats.remed, stats.participated)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default SectionPerformanceTable;
